/**
 * Tear sheet product-info fields tracked for authorization review.
 * `get` reads the live catalog row; the approved snapshot (from the backend) stores the same
 * keys as plain scalars, so `snapshot[key]` is directly comparable to `get(row)`.
 */
const TEAR_SHEET_FIELDS = [
  { key: 'productName', label: 'Product name', get: (row) => row?.productName },
  { key: 'skuId', label: 'SKU ID', get: (row) => row?.skuId },
  { key: 'hsCode', label: 'HS code', get: (row) => row?.hsCode },
  { key: 'length', label: 'Length', get: (row) => row?.length },
  { key: 'breadth', label: 'Breadth', get: (row) => row?.breadth },
  { key: 'height', label: 'Height', get: (row) => row?.height },
  { key: 'dimensionUnit', label: 'Dimension unit', get: (row) => row?.dimensionUnit },
  { key: 'netWeight', label: 'Net weight', get: (row) => row?.netWeight },
  { key: 'weightUnit', label: 'Weight unit', get: (row) => row?.weightUnit },
  { key: 'moq', label: 'MOQ', get: (row) => row?.moq },
  { key: 'tearSheetCost', label: 'Cost', get: (row) => row?.tearSheetCost },
  { key: 'currency', label: 'Currency', get: (row) => row?.currency?.name },
  { key: 'barcodeType', label: 'Barcode type', get: (row) => row?.barcodeType?.name },
];

const normalizeValue = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  if (Array.isArray(value)) {
    return value.map((item) => normalizeValue(item)).join(', ');
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  // Compare numbers by value so 12 and "12.00" (from decimal casts) are treated as equal.
  if (typeof value === 'number' || (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value)))) {
    return String(Number(value));
  }

  return String(value);
};

export const valuesEqual = (left, right) => normalizeValue(left) === normalizeValue(right);

const formatDisplay = (value) => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  return String(value);
};

/**
 * Diff the current catalog row against the snapshot captured when it was last approved.
 * Returns one entry per changed field: { key, label, oldValue, newValue }.
 * With no snapshot (never approved) there is no baseline, so nothing is returned.
 */
export const getChangedTearSheetFields = (currentRow, approvedSnapshot) => {
  if (!currentRow || !approvedSnapshot) {
    return [];
  }

  return TEAR_SHEET_FIELDS.reduce((changes, field) => {
    const newValue = field.get(currentRow);
    const oldValue = approvedSnapshot[field.key];

    if (!valuesEqual(newValue, oldValue)) {
      changes.push({
        key: field.key,
        label: field.label,
        oldValue: formatDisplay(oldValue),
        newValue: formatDisplay(newValue),
      });
    }

    return changes;
  }, []);
};

export const buildAuthorizationRowKey = (row) => {
  if (!row) {
    return 'unknown';
  }
  return `${row.clientId || 'client'}:${row.uid || row.productUid || row.itemNo || 'item'}`;
};
